import React from "react";
import { Field, JsonEditor, NumberInput, optionsTip, Section, SelectInput, TextInput, Toggle } from "../common";
import { GLOSSARY } from "../glossary";
import { setIn } from "../helpers";

const ROLE_TIP = optionsTip(GLOSSARY.role.role, GLOSSARY.sources.roles, { primary: "Primary", enrichment: "Enrichment" });
const MATCH_TIP = optionsTip(GLOSSARY.role.matchOn, GLOSSARY.sources.matchOn, { "product.key": "Product key", "variant.key": "Variant key", "variant.field": "Variant field" });

// Step 4: role in the merge and what happens when the source fails.
const RoleStep = ({ source, update, draft }) => {
    const variantFields = draft.model?.variant_fields || [];
    const matchOptions = [
        { value: "product.key", label: "Product key" },
        { value: "variant.key", label: "Variant key" },
        ...variantFields.filter((f) => f.role !== "key").map((f) => ({ value: `variant.${f.key}`, label: `Variant field: ${f.key}` })),
    ];
    const required = source.failure?.required ?? source.role === "primary";

    return (
        <div className="flex flex-col gap-4 max-w-3xl">
            <Section title="Source">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Name" tip={GLOSSARY.role.name}>
                        <TextInput value={source.name} onChange={(name) => update((s) => ({ ...s, name }))} />
                    </Field>
                    <Field label="Id" help="Used by dependent calls and lookups. Letters, digits, - and _." tip={GLOSSARY.role.id}>
                        <TextInput monospace value={source.id} onChange={(id) => update((s) => ({ ...s, id: id.replace(/[^A-Za-z0-9_-]/g, "") }))} />
                    </Field>
                </div>
            </Section>

            <Section title="Role">
                <Field label="Role" help="Primary sources create products. Enrichment sources only add fields and variants to products that already exist." tip={ROLE_TIP}>
                    <SelectInput value={source.role} options={[{ value: "primary", label: "Primary" }, { value: "enrichment", label: "Enrichment" }]} onChange={(role) => update((s) => ({ ...s, role }))} />
                </Field>
                {source.role === "enrichment" && (
                    <Field label="Match records on" help="Unmatched records are reported in the run details." tip={MATCH_TIP}>
                        <SelectInput value={source.enrichment?.match_on || "product.key"} options={matchOptions} onChange={(v) => update((s) => setIn(s, ["enrichment", "match_on"], v))} />
                    </Field>
                )}
            </Section>

            <Section title="When this source fails">
                <Toggle
                    checked={required}
                    onChange={(value) => update((s) => setIn(s, ["failure", "required"], value))}
                    label="Required"
                    help="A required source that fails fails the whole run, so nothing is pushed and Target keeps what it has."
                    tip={GLOSSARY.role.required}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Minimum products" help="Fewer than this counts as a failure (default 1)" tip={GLOSSARY.role.minRecords}>
                        <NumberInput value={source.failure?.min_records} min={0} onChange={(v) => update((s) => setIn(s, ["failure", "min_records"], v))} />
                    </Field>
                </div>
            </Section>

            <details>
                <summary className="text-sm font-medium text-primary cursor-pointer">Edit the whole source as JSON</summary>
                <JsonEditor rows={24} value={source} onChange={(v) => v && typeof v === "object" && update(v)} className="mt-2" />
            </details>
        </div>
    );
};

export default RoleStep;
